import R from "ramda";
import moment from "moment";

export const filtersToUrlParams = ({
  endDate,
  exportType,
  isExports,
  isTrash,
  orderBy,
  orderDirection,
  processingState,
  selectedCardNumbers,
  selectedCategories,
  selectedCurrencies,
  selectedEnvelopes,
  selectedName,
  selectedPaymentTypes,
  selectedSearchQueryString,
  selectedVendors,
  startDate,
  totalMin,
  totalMax,
  type,
  viewedStatus
}, pagination) => (
  limitQuery(pagination.limit)
    .concat(offsetQuery(pagination))
    .concat("&include_total_sum_filtered=true")
    .concat(documentTypeQuery(type))
    .concat(showTrashQuery(isTrash))
    .concat(processingStateQuery(processingState))
    .concat(orderQuery(orderBy, orderDirection))
    .concat(startDateQuery(startDate))
    .concat(endDateQuery(endDate))
    .concat(currencyQuery(selectedCurrencies))
    .concat(vendorsQuery(selectedVendors))
    .concat(categoriesQuery(selectedCategories))
    .concat(paymentTypesQuery(selectedPaymentTypes))
    .concat(cardNumbersQuery(selectedCardNumbers))
    .concat(envelopesQuery(selectedEnvelopes))
    .concat(viewedQuery(viewedStatus))
    .concat(exportTypeQuery(exportType))
    .concat(totalMinQuery(totalMin))
    .concat(totalMaxQuery(totalMax))
    .concat(searchQueryStringQuery(selectedSearchQueryString))
    .concat(selectedNameQuery(selectedName))
    .concat(exportsPageQuery(isExports))
);

export const reportFilterStringGenerator = ({ categories, endDate, startDate, vendors }) => (
  "trashed=false&processing_state=PROCESSED"
    .concat(startDateQuery(startDate))
    .concat(endDateQuery(endDate))
    .concat(categoriesQuery(categories))
    .concat(vendorsQuery(vendors))
    .concat("&order_by_asc=issued")
);

const offset = ({ limit, pageNumber }) => (pageNumber - 1) * limit;
const offsetQuery = (pagination) => `&offset=${offset(pagination)}`;

const encodeString = (value) => encodeURIComponent(value).replace(/%20/g, "+");

const limitQuery = (limit) => `limit=${limit}`;

const processingStateQuery = (processingState) => {
  if (processingState === null || processingState === undefined) {
    return "";
  }
  if (typeof processingState === "object") {
    return `&or_processing_state=${processingState[0]}&or_processing_state=${processingState[1]}`;
  }
  return `&processing_state=${processingState}`;
};

const orderQuery = (orderBy, orderDirection) => {
  if (orderBy === "paymentType" && orderDirection) {
    const newOrderBy = "payment_type";
    const newOrderDirection = orderDirection === "ASC" ? "asc" : "desc";
    return `&order_by_${newOrderDirection}=${newOrderBy}&order_by_${newOrderDirection}=last_four_digits`;
  }
  if (orderBy && orderDirection) {
    return `&order_by_${orderDirection === "ASC" ? "asc" : "desc"}=${orderBy}`;
  }
  return "";
};

const startDateQuery = (startDate) => dateQuery(startDate, "start");
const endDateQuery = (endDate) => dateQuery(endDate, "end");

const dateQuery = (date, type) => {
  if (date === null || date === undefined) {
    return "";
  }
  const newDate = moment(date);
  const minMax = type === "start" ? "min" : "max";
  const uriDate = newDate.format("YYYY-MM-DDTHH:mm:ss[Z]");
  return `&issued_${minMax}=${uriDate}`;
};

const vendorsQuery = (selectedVendors) => {
  if ((selectedVendors || []).length === 0) {
    return "";
  }
  return selectedVendors.reduce((prev, curr) => `${prev}&or_vendor=${encodeString(curr)}`, "");
};

const categoriesQuery = (selectedCategories) => {
  if ((selectedCategories || []).length === 0) {
    return "";
  }
  if (R.indexOf("No category", selectedCategories) > -1) {
    return "&is_empty=category";
  }
  return selectedCategories.reduce((prev, curr) => (`${prev}&or_category=${encodeString(curr)}`), "");
};

const viewedQuery = (viewed) => {
  if (viewed === null || viewed === undefined || viewed === "") {
    return "";
  }
  return `&viewed=${viewed === "viewed"}`;
};

const totalQuery = (total, type) => {
  if (total === "" || total === null || total === undefined) {
    return "";
  }
  return `&total_${type}=${total}`;
};

const paymentTypesQuery = (selectedPaymentTypes) => {
  if ((selectedPaymentTypes || []).length === 0) {
    return "";
  }

  if (selectedPaymentTypes[0] === "none") {
    return "&is_empty=payment_type";
  }

  return selectedPaymentTypes.reduce((prev, curr) => (`${prev}&or_payment_type=${encodeString(curr)}`), "");
};

const cardNumbersQuery = (cards) => {
  if ((cards || []).length === 0) {
    return "";
  }
  return `&last_four_digits=${encodeString(cards)}`;
};

const currencyQuery = (selectedCurrencies) => {
  if ((selectedCurrencies || []).length === 0) {
    return "";
  }
  return selectedCurrencies.reduce((prev, curr) => (`${prev}&or_currency=${encodeString(curr)}`), "");
};

const exportTypeQuery = (exportType) => {
  if (exportType === null || exportType === undefined) {
    return "";
  }

  switch (exportType) {
    case "csv":
      return "&or_export_history_to=csv&or_export_history_to=CSV";
    case "not-csv":
      return "&not_export_history_to=csv&not_export_history_to=CSV";
    case "pdf":
      return "&export_history_to=pdf";
    case "not-pdf":
      return "&not_export_history_to=pdf";
    case "accounting":
      return "&or_category=Exported&or_category=Do+not+export&or_category=XERO"
             + "&or_category=SAASU&or_category=QB&or_category=QuickBooks+Online";
    case "not-accounting":
      return "&not_category=Exported&not_category=Do+not+export&not_category=XERO"
             + "&not_category=SAASU&not_category=QB&not_category=QuickBooks+Online";
    default:
      return "";
  }
};

const envelopesQuery = (selectedEnvelopes) => {
  if ((selectedEnvelopes || []).length === 0) {
    return "";
  }
  if (selectedEnvelopes.indexOf("none") > -1) {
    return "&is_empty=envelope";
  }
  return selectedEnvelopes.reduce((prev, curr) => (`${prev}&or_envelope=${encodeString(curr)}`), "");
};

const totalMinQuery = (total) => totalQuery(total, "min");
const totalMaxQuery = (total) => totalQuery(total, "max");

const exportsPageQuery = (isExports) => {
  if (isExports) {
    return "&not_category=Exported&not_category=Do+not+export";
  }
  return "";
};

const documentTypeQuery = (type) => {
  switch (type) {
    case "receipt":
      return "&type=receipt";
    case "other":
      return "&type=other";
    default:
      return "";
  }
};

const searchQueryStringQuery = (query) => {
  if (query === null || query === undefined || query === "") {
    return "";
  }
  return `&q=${query}`;
};

const showTrashQuery = (isTrash) => {
  if (isTrash) {
    return "&trashed=true";
  }
  return "&trashed=false";
};

const selectedNameQuery = (name) => {
  if (name) {
    return `&name=${name}`;
  }
  return "";
};



// WEBPACK FOOTER //
// ./src/js/api/filters-to-url-params.js